/**
 * ActionDetailSummary Component
 *
 * Displays summary statistics for filtered actions.
 *
 * Three modes:
 * 1. Default (no expanded groups): Single-row grid with stat cells
 * 2. Single-level: Multi-row table with Overall + per-value breakdown rows
 * 3. Hierarchical: Multi-level nested table with depth-based indentation
 *
 * Two column groups separated by a visual break:
 * Action stats: Actions | aPPP | ePPP (if available) | ORB% | TOV%
 * Shot stats:   Shots | 2PT% | 3PT% | TS% | Rim Rate | Mid Rate | 3PT Rate
 *
 * Zone rates (Rim/Mid/3PT) calculated from the shots array,
 * linked to actions via chanceId. Per-zone TS% shown as sub-values.
 */

import { ReactNode, useMemo } from "react";
import { IconButton, Tooltip, CircularProgress } from "@mui/material";
import PlayCircleOutline from "@mui/icons-material/PlayCircleOutline";
import KeyboardDoubleArrowRight from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Action, ActionDetailSummaryProps, Shot } from "./types";
import { applyFilters as applyShotFilters } from "../ShotDetailModal/utils";
import {
  applyActionFilters,
  formatGroupLabel,
  formatGroupValue,
  calculateRowStats,
  buildHierarchicalRows,
  stripLineupFilters,
  RowStats,
  HierarchicalRow,
} from "./utils";
import { isLineupKey, LineupKey } from "./types";
import { usePlayerTable } from "./PlayerTableContext";
import { useLeagueEnvironment } from "../../data/LeagueEnvironmentProvider";
import { glossaryGuidePath } from "../../models/Glossary";
import "../ShotDetailModal/ShotDetailModal.scss";

// ============================================================================
// Formatters
// ============================================================================

const fmtPct = (v: number) => `${(v * 100).toFixed(1)}%`;
const fmtPer100 = (v: number) => (v * 100).toFixed(1);
const fmtTs = (v: number | null) =>
  v !== null ? `${(v * 100).toFixed(1)}% TS` : "--";
const fmtRate = (v: number) => (v * 100).toFixed(1);

// ============================================================================
// Column header help
// ============================================================================

const POINTS_GUIDE = "points-measures";

/**
 * aPPP / ePPP are per chance with a rebound credit, which narrows any lineup's gap
 * (the lineup filters invite exactly that comparison), so the header says what they
 * are and links to the glossary guide. Opens in a new tab: the chart's filters stay put.
 */
function PointsHeader({ label, text }: { label: string; text: string }) {
  const environment = useLeagueEnvironment();
  const title: ReactNode = (
    <span style={{ display: "block", fontSize: "0.75rem", lineHeight: 1.45 }}>
      {text}{" "}
      <a
        href={glossaryGuidePath(environment, POINTS_GUIDE)}
        target="_blank"
        rel="noopener noreferrer"
        style={{ color: "#fff", textDecoration: "underline" }}
      >
        How the points measures differ
      </a>
    </span>
  );
  return (
    <th>
      <Tooltip title={title} arrow placement="top">
        <span style={{ borderBottom: "1px dotted currentColor", cursor: "help" }}>{label}</span>
      </Tooltip>
    </th>
  );
}

const APPP_HELP =
  "Adjusted points per chance, shown per 100: each chance's points plus the average value of what it set up " +
  "(an offensive rebound off a miss, the opponent's next start), with free throws at the shooter's usual " +
  "percentage. Built for comparing actions. For a lineup's net rating, use PP100 in the Explorer.";
const EPPP_HELP =
  "Expected aPPP: the same value from shot quality and the shooter's make probability instead of makes and misses.";

// ============================================================================
// Component
// ============================================================================

export default function ActionDetailSummary({
  actions,
  linkedShots,
  rootLinkedShots,
  lineups,
  filters,
  shotFilters,
  expandedGroups,
  onRowHover,
  onRowClick,
  selectedRowKey,
  showEPts = true,
  onVideoClick,
  videoLoading,
}: ActionDetailSummaryProps) {
  const players = usePlayerTable();
  const { overallStats, hierarchicalRows, filteredActions } = useMemo(() => {
    const filtered = applyActionFilters(actions, filters, lineups);

    // Apply shot filters to the linked shot pool.
    // Shooting metrics (TS%, zone rates) reflect both action + shot filters.
    const shotFilteredPool = applyShotFilters(linkedShots, shotFilters);
    const rootShotFilteredPool = applyShotFilters(rootLinkedShots, shotFilters);

    // When any lineup key is in the breakdown, sub-rows operate on a pool
    // where the in-breakdown lineup filters are stripped (but out-of-breakdown
    // lineup filters still apply). Overall should reflect the same pool.
    const inBreakdownLineupKeys = expandedGroups.filter(isLineupKey) as LineupKey[];
    const hasLineupBreakdown = inBreakdownLineupKeys.length > 0;
    const overallActions = hasLineupBreakdown
      ? applyActionFilters(
          actions,
          stripLineupFilters(filters, inBreakdownLineupKeys),
          lineups,
        )
      : filtered;
    const overallShotPool = hasLineupBreakdown
      ? rootShotFilteredPool
      : shotFilteredPool;

    const overall = calculateRowStats(overallActions, overallShotPool);

    let rows: HierarchicalRow[] = [];
    if (expandedGroups.length > 0) {
      rows = buildHierarchicalRows(filtered, shotFilteredPool, expandedGroups, {
        lineups,
        filters,
        players,
        rootActions: actions,
        rootLinkedShots: rootShotFilteredPool,
      });
    }

    return {
      overallStats: overall,
      hierarchicalRows: rows,
      filteredActions: overallActions,
    };
  }, [actions, linkedShots, rootLinkedShots, lineups, filters, shotFilters, expandedGroups, players]);

  const totalActions = overallStats.actionCount;
  const hasBreakdown = expandedGroups.length > 0;
  const headerLabel = hasBreakdown
    ? expandedGroups
        .map(formatGroupLabel)
        .reduce<React.ReactNode[]>((acc, label, i) => {
          if (i > 0)
            acc.push(
              <KeyboardDoubleArrowRight
                key={`sep-${i}`}
                sx={{
                  fontSize: 14,
                  verticalAlign: "middle",
                  mx: 0.25,
                  opacity: 0.7,
                }}
              />,
            );
          acc.push(<span key={i}>{label}</span>);
          return acc;
        }, [])
    : "Summary";

  return (
    <div
      className={`summary-table-scroll${hasBreakdown ? " summary-table-scroll--breakdown" : ""}`}
    >
      <table className="summary-table">
        <thead>
          <tr>
            <th className="summary-table__label">{headerLabel}</th>
            <th>Actions</th>
            {/* aPP100 / ePP100: per valid chance, not per possession like the Explorer's PPP */}
            <PointsHeader label="aPPP" text={APPP_HELP} />
            {showEPts && <PointsHeader label="ePPP" text={EPPP_HELP} />}
            <th>ORB%</th>
            <th>TOV%</th>
            <th className="summary-table__separator" />
            <th>Shots</th>
            <th>2PT%</th>
            <th>3PT%</th>
            <th>TS%</th>
            <th>Rim Rate</th>
            <th>Mid Rate</th>
            <th>3PT Rate</th>
          </tr>
        </thead>
        <tbody>
          {/* Overall row */}
          <SummaryRow
            label="Overall"
            stats={overallStats}
            isOverall
            parentActionCount={totalActions}
            depth={0}
            hasBreakdown={hasBreakdown}
            showEPts={showEPts}
            actions={filteredActions}
            onVideoClick={onVideoClick}
            videoLoading={videoLoading}
          />

          {/* Hierarchical breakdown rows */}
          {hierarchicalRows.map((row) => (
            <SummaryRow
              key={row.rowKey}
              label={row.label}
              stats={row.stats}
              parentActionCount={row.parentActionCount}
              depth={row.depth}
              isGroupHeader={row.isGroupHeader}
              isShotBreakdown={row.isShotBreakdown}
              isOverlapping={row.isOverlapping}
              rowKey={row.rowKey}
              actions={row.actions}
              shots={row.shots}
              isSelected={selectedRowKey === row.rowKey}
              onHover={onRowHover}
              onClick={onRowClick}
              showEPts={showEPts}
              onVideoClick={onVideoClick}
              videoLoading={videoLoading}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Table Row
// ============================================================================

interface SummaryRowProps {
  label: string;
  stats: RowStats;
  isOverall?: boolean;
  isGroupHeader?: boolean;
  hasBreakdown?: boolean;
  parentActionCount: number;
  depth: number;
  rowKey?: string;
  actions?: Action[];
  shots?: Shot[];
  isSelected?: boolean;
  isShotBreakdown?: boolean;
  isOverlapping?: boolean;
  showEPts?: boolean;
  onHover?: (
    rowKey: string | null,
    actions: Action[] | null,
    shots?: Shot[],
  ) => void;
  onClick?: (rowKey: string, actions: Action[], shots?: Shot[]) => void;
  onVideoClick?: (
    chanceIds: string[],
    rowLabel: string,
    metaKey?: boolean,
  ) => void;
  videoLoading?: boolean;
}

function SummaryRow({
  label,
  stats,
  isOverall,
  isGroupHeader,
  hasBreakdown,
  parentActionCount,
  depth,
  rowKey,
  actions,
  shots,
  isSelected,
  isShotBreakdown,
  isOverlapping,
  showEPts,
  onHover,
  onClick,
  onVideoClick,
  videoLoading,
}: SummaryRowProps) {
  const pctOfParent =
    parentActionCount > 0 ? (stats.actionCount / parentActionCount) * 100 : 0;
  // For shot breakdown rows, compute % from shotCount / parentCount
  const shotPctOfParent =
    parentActionCount > 0 ? (stats.shotCount / parentActionCount) * 100 : 0;

  // Build CSS class list
  let rowClass = "summary-table__row";
  if (isOverall)
    rowClass = hasBreakdown
      ? "summary-table__overall"
      : "summary-table__overall summary-table__overall--no-breakdown";
  if (isGroupHeader) rowClass += " summary-table__group-header";
  if (depth >= 2) rowClass += ` summary-table__depth-${Math.min(depth, 3)}`;
  if (isSelected) rowClass += " summary-table__row--selected";

  const labelPadding = isOverall ? 8 : 8 + depth * 16;
  const isInteractive = !isOverall && rowKey && actions;

  // Compute chance IDs for video: shot breakdowns use shots, action breakdowns use actions
  const getChanceIds = () => {
    if (isShotBreakdown && shots) {
      const ids = shots
        .map((s) => s.chanceId)
        .filter((id): id is string => id !== null);
      return Array.from(new Set(ids));
    }
    if (actions) {
      const ids = actions
        .map((a) => a.chanceId)
        .filter((id): id is string => id !== null);
      return Array.from(new Set(ids));
    }
    return [];
  };

  // Build full label path from rowKey (e.g. "primaryPlayer:Avdija, Deni|pickType:MPR" → "Avdija, Deni  »  MPR")
  const getFullLabel = () => {
    if (isOverall || !rowKey) return label;
    return rowKey
      .split("|")
      .map((segment) => {
        const colonIdx = segment.indexOf(":");
        const key = segment.slice(0, colonIdx);
        const value = segment.slice(colonIdx + 1);
        return formatGroupValue(key, value);
      })
      .join("  \u00BB  ");
  };

  const handleVideoClick = onVideoClick
    ? (e: React.MouseEvent) => {
        e.stopPropagation();
        const ids = getChanceIds();
        if (ids.length > 0) onVideoClick(ids, getFullLabel(), e.metaKey);
      }
    : undefined;

  return (
    <tr
      className={rowClass}
      onMouseEnter={
        isInteractive ? () => onHover?.(rowKey!, actions!, shots) : undefined
      }
      onMouseLeave={isInteractive ? () => onHover?.(null, null) : undefined}
      onClick={
        isInteractive ? () => onClick?.(rowKey!, actions!, shots) : undefined
      }
      style={{ cursor: isInteractive ? "pointer" : undefined }}
    >
      <td
        className="summary-table__label"
        style={{ paddingLeft: labelPadding }}
      >
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          {label}
          {handleVideoClick &&
            (videoLoading ? (
              <CircularProgress
                size={14}
                sx={{ color: "text.secondary", ml: 0.5 }}
              />
            ) : (
              <Tooltip title="Video" arrow placement="top">
                <IconButton
                  size="small"
                  onClick={handleVideoClick}
                  sx={{
                    p: "2px",
                    color: "text.secondary",
                    opacity: 0.4,
                    "&:hover": { opacity: 1, color: "primary.main" },
                    transition: "opacity 0.15s, color 0.15s",
                  }}
                >
                  <PlayCircleOutline sx={{ fontSize: "0.95rem" }} />
                </IconButton>
              </Tooltip>
            ))}
        </span>
      </td>

      {/* Action stats: Actions | aPPP | ePPP | ORB% | TOV% */}
      {isShotBreakdown ? (
        <>
          <td>{"\u2014"}</td>
          <td>{"\u2014"}</td>
          {showEPts && <td>{"\u2014"}</td>}
          <td>{"\u2014"}</td>
          <td>{"\u2014"}</td>
        </>
      ) : (
        <>
          <td>
            {stats.actionCount.toLocaleString()}
            {!isOverall && (
              <span
                className="summary-table__sub"
                style={isOverlapping ? { fontStyle: "italic" } : undefined}
                title={
                  isOverlapping
                    ? "Rows overlap — same action can appear in multiple rows; totals exceed parent"
                    : undefined
                }
              >
                ({pctOfParent.toFixed(1)}%
                {isOverlapping ? "\u00A0of pool" : ""})
              </span>
            )}
          </td>
          <td>{fmtPer100(stats.aPPP)}</td>
          {showEPts && <td>{fmtPer100(stats.ePPP)}</td>}
          <td>{stats.orbPct !== null ? fmtPct(stats.orbPct) : "--"}</td>
          <td>{fmtPct(stats.tovPct)}</td>
        </>
      )}

      {/* Separator */}
      <td className="summary-table__separator" />

      {/* Shot stats: Shots | 2PT% | 3PT% | TS% | Rim Rate | Mid Rate | 3PT Rate */}
      <td>
        {isShotBreakdown ? (
          <>
            {stats.shotCount.toLocaleString()}
            <span className="summary-table__sub">
              ({shotPctOfParent.toFixed(1)}%)
            </span>
          </>
        ) : (
          stats.shotCount.toLocaleString()
        )}
      </td>
      <td>{stats.twoPtPct !== null ? fmtPct(stats.twoPtPct) : "--"}</td>
      <td>{stats.threePtPct !== null ? fmtPct(stats.threePtPct) : "--"}</td>
      <td>{stats.tsPct !== null ? fmtPct(stats.tsPct) : "--"}</td>
      <td>
        {fmtRate(stats.rimRate)}
        <span className="summary-table__sub">{fmtTs(stats.rimTs)}</span>
      </td>
      <td>
        {fmtRate(stats.midRate)}
        <span className="summary-table__sub">{fmtTs(stats.midTs)}</span>
      </td>
      <td>
        {fmtRate(stats.threeRate)}
        <span className="summary-table__sub">{fmtTs(stats.threeTs)}</span>
      </td>
    </tr>
  );
}
